import * as Yup from 'yup'
import { buildOfferRecipientsValidation } from './offerRecipientYupValidation'
import {
  amountGreaterThanZeroMsg,
  awsAccountIdFieldValidationMessage,
  awsRegexValidationMessage,
  daysDurationMessage,
  duplicateDateMessage,
  emptySpacesMessage,
  enterAmountMessage,
  enterNumberMessage,
  enterNumberMsg,
  enterUsageDuration,
  mandatoryFieldMessage,
  missignFieldMessage,
  monthsDurationMessage,
  onePaymentBeforeServiceDateMsg,
  paymentDateValidationMessage,
  threeDecimalMsg,
  enterPriceMsg,
  enterQuantityMsg,
  productDimMessage,
  quantityDecimalMsg,
  quantityMoreThan0Msg,
  quantityNegativeMsg,
  RENEWAL_PERCENT_SUM_ERROR,
  RENEWAL_MONTHS_AFTER_REQUIRED,
  THIS_FIELD_IS_REQUIRED,
  threeDecimalMessage,
  usageDurationMoreThan0Msg,
  usageDurationDecimalMsg,
  usageDurationNegativeMsg,
  valueGreaterThanZeroMsg,
} from './messagesContants'

import {
  awsAccountIdRegex,
  dyasDurationRegex,
  emptySpaceCharsAtStartAndEndRegex,
  monthsDurationRegex,
} from './constants'

import { validateEmptyValueAndNewLines } from './validateEmptyValueAndNewLines'
import {
  ContainerPricingModel,
  containerProductHourlyAndUsage,
} from '../../modules/productsListing/types'
import { EulaType, Installments, PaymentType } from '../../types/flyout'
import { DateTime } from 'luxon'
import _, { isEmpty, isNil } from 'lodash'

import { isNonEmptyNumber } from '@labrav/common'
import { PlanType } from '@labrav/react-components'
import './globalValidations'
import { getDaysFromServiceLength } from './getDaysFromServiceLength'
import { isRenewalMonthsAfterFilled } from '../components/PaymentScheduleV2/renewalSchedule'

export { getDaysFromServiceLength } from './getDaysFromServiceLength'

const OPT_OUT_DEADLINE_LT_DURATION_MSG =
  'Opt out deadline must be less than the contract duration.'

const getContractDurationDays = ({
  serviceLength,
  isContract,
}: {
  serviceLength?: string | number | null
  isContract: boolean
}): number => {
  if (isNil(serviceLength) || serviceLength === '') return 0

  if (isContract) {
    const months = Number(
      typeof serviceLength === 'string'
        ? String(serviceLength).replace(/[DMYH]/gi, '')
        : serviceLength
    )
    if (!months || isNaN(months)) return 0
    return Math.ceil(
      DateTime.now().plus({ months }).diff(DateTime.now(), 'days').days
    )
  }

  if (typeof serviceLength === 'number') return serviceLength

  return getDaysFromServiceLength({
    containerOfferServiceLengthValue: serviceLength,
  })
}

export type CustomStakeholder = {
  email: string
}

declare module 'yup' {
  interface ArraySchema<T> {
    uniqueProperty(propertyPath: string, message?: string): ArraySchema<T>
    installmentsValidation(
      propertyPath: string,
      message?: string
    ): ArraySchema<T>
    amiPaymentDateValidation(
      propertyPath: string,
      message?: string
    ): ArraySchema<T>
    uniquePropertyCustomType(
      propertyPath: string,
      message?: string,
      dimensions?: any
    ): ArraySchema<T>
  }
}

declare module 'yup' {
  interface NumberSchema {
    usageDurationValidation({
      propertyPath,
    }: {
      propertyPath: string
    }): NumberSchema
  }
}

Yup.addMethod(
  Yup.array,
  'uniquePropertyCustomType',
  function (propertyPath, message, dimensions = []) {
    return this.test('unique', '', function (list: any) {
      const errors: any[] = []

      list?.forEach((item: any, index: number) => {
        const propertyValue = _.get(item, propertyPath)
        const normalizedValue =
          typeof propertyValue === 'string'
            ? propertyValue.toLowerCase()
            : propertyValue
        const type = item?.type

        if (
          type === 'custom' &&
          propertyValue &&
          _.filter(list, (obj: any) => {
            const otherValue = _.get(obj, propertyPath)
            const normalizedOtherValue =
              typeof otherValue === 'string'
                ? otherValue.toLowerCase()
                : otherValue
            return (
              obj?.type === 'custom' && normalizedOtherValue === normalizedValue
            )
          }).length > 1
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }

        if (
          type === 'custom' &&
          propertyValue &&
          dimensions
            .map((v: string) => v.toLowerCase())
            .includes(normalizedValue)
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }
      })

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  }
)

Yup.addMethod(Yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list: any) {
    const errors: any = []

    list?.forEach((item: any, index: number) => {
      const propertyValue = _.get(item, propertyPath)
      if (
        propertyValue &&
        _.filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          })
        )
      }
    })

    if (!_.isEmpty(errors)) {
      throw new Yup.ValidationError(errors)
    }

    return true
  })
})

const containerOfferRecipientMessages = {
  firstNameRequired: missignFieldMessage,
  lastNameRequired: missignFieldMessage,
  emailRequired: 'Please provide the email of your offer recipient. ',
  emailInvalid: 'Enter valid email.',
  duplicateEmail: 'Duplicate emails are not allowed.',
}

const amiOfferRecipientMessages = {
  ...containerOfferRecipientMessages,
  emailRequired: 'Provide the email of your offer recipient. ',
}

export const offerRecipientValidation = Yup.object().shape({
  offerRecipients: buildOfferRecipientsValidation(
    containerOfferRecipientMessages
  ),
})
export const privateOfferStakeholdersValidationSchema = Yup.object().shape({
  offerStakeholders: Yup.array()
    .of(
      Yup.object().shape({
        email: Yup.string().required('Email cannot be empty.'),
      })
    )
    .min(1, 'At least one stakeholder is required.'),

  customOfferStakeholders: Yup.array().test(
    'Enter-valid-emails',
    'Enter valid emails',
    function (value?: CustomStakeholder[]) {
      if (!value) return true
      return value?.every(v =>
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          v.email
        )
      )
    }
  ),
})

export const privateOfferCustomStakeholdersValidationSchema =
  Yup.object().shape({
    customOfferStakeholders: Yup.array().test(
      'Enter-valid-emails',
      'Enter valid emails',
      function (value?: CustomStakeholder[]) {
        if (!value) return true
        return value?.every(v =>
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
            v.email
          )
        )
      }
    ),
  })

export const offerRecipientReminderValidation = Yup.object().shape({
  customMessage: Yup.string()
    .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
    .test(
      'contains-new-line-characters',
      `${emptySpacesMessage}`,
      validateEmptyValueAndNewLines
    ),
})

export const metaDataItemValidationSchema = Yup.object().shape({
  key: Yup.string().matches(
    emptySpaceCharsAtStartAndEndRegex,
    `${mandatoryFieldMessage}.`
  ),
  value: Yup.string().matches(
    emptySpaceCharsAtStartAndEndRegex,
    `${mandatoryFieldMessage}.`
  ),
})
const getTotalMonths = (paymentType: PaymentType, serviceLength: number) => {
  if (paymentType === PaymentType.MONTHLY) {
    return serviceLength
  } else if (paymentType === PaymentType.QUARTERLY) {
    return 3 * serviceLength
  } else if (paymentType === PaymentType.ANNUALLY) {
    return 12 * serviceLength
  }
}
const metricPricing = Yup.array().when(['individualDiscount'], {
  is: (individualDiscount: boolean) => {
    return individualDiscount
  },
  then: Yup.array().of(
    Yup.object().shape({
      discount: Yup.number()
        .typeError('Please enter a number')
        .checkDecimalpointValidation({
          message: 'Only 2 decimal places allowed.',
        })
        .required('Please enter a number'),
    })
  ),
})

metaDataItemValidationSchema.fields.key =
  metaDataItemValidationSchema.fields.key.when('value', {
    is: (value: string) => value && value.trim().length > 0,
    then: Yup.string()
      .required('Enter a non-empty value')
      .matches(emptySpaceCharsAtStartAndEndRegex, 'Enter a non-empty value.'),
    otherwise: Yup.string().matches(
      emptySpaceCharsAtStartAndEndRegex,
      'Enter a non-empty value.'
    ),
  })

metaDataItemValidationSchema.fields.value =
  metaDataItemValidationSchema.fields.value.when('key', {
    is: (key: string) => key && key.trim().length > 0,
    then: Yup.string()
      .required('Enter a non-empty value')
      .matches(emptySpaceCharsAtStartAndEndRegex, 'Enter a non-empty value.'),
    otherwise: Yup.string().matches(
      emptySpaceCharsAtStartAndEndRegex,
      'Enter a non-empty value.'
    ),
  })

Yup.addMethod(
  Yup.array,
  'installmentsValidation',
  function (propertyPath, timeZone, message) {
    return this.test('installmentsValidation', '', function (list: any) {
      const errors: any = []
      const offerExpirationDate = this.options?.context?.offerExpirationDate
      const paymentDatesBeforeExpirationDate = _.filter(
        list,
        (item: Installments) =>
          DateTime.fromISO(item.paymentDate, { zone: timeZone }) <=
          DateTime.fromISO(offerExpirationDate, { zone: timeZone })
      ).map(item => item.paymentDate)

      list?.forEach((item: any, index: number) => {
        if (
          paymentDatesBeforeExpirationDate.length >= 2 &&
          paymentDatesBeforeExpirationDate.includes(item.paymentDate)
        ) {
          errors.push(
            this.createError({
              path: `${this.path}[${index}].${propertyPath}`,
              message,
            })
          )
        }
      })

      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }

      return true
    })
  }
)

const timeZone = 'UTC'

Yup.addMethod(
  Yup.number,
  'usageDurationValidation',
  function ({ propertyPath }) {
    return this.test('usageDurationValidation', function (value) {
      if (!value) return true
      const errors: any = []
      const containerOfferServiceLengthValue =
        this.options?.context?.containerOfferServiceLength
      const offerExpirationDate = this.parent.offerExpirationDate

      if (!isNil(value)) {
        const validDays = getValidUsageDurationCount({
          offerExpirationDate,
          containerOfferServiceLengthValue,
        })
        if (value <= validDays) {
          errors.push(
            this?.createError({
              path: propertyPath,
              message: `please enter number greater than ${validDays}`,
            })
          )
        }
      }
      if (!_.isEmpty(errors)) {
        throw new Yup.ValidationError(errors)
      }
      return true
    })
  }
)

export const getValidUsageDurationCount = ({
  offerExpirationDate,
  containerOfferServiceLengthValue,
}: {
  offerExpirationDate?: string
  containerOfferServiceLengthValue?: string | number
}) => {
  const dayDiffForExpiration = !isNil(offerExpirationDate)
    ? Math.ceil(
        DateTime.fromISO(offerExpirationDate).diff(DateTime.now(), 'days').days
      )
    : 0
  return (
    dayDiffForExpiration +
    1 +
    getDaysFromServiceLength({
      containerOfferServiceLengthValue,
    })
  )
}

export function generateSubscriptionEndDateSchema(
  planType: PlanType | undefined
) {
  if (planType === PlanType.SAAS_SUBSCRIPTION) {
    return Yup.string()
      .nullable()
      .required('Subscription end date is required')
      .test(
        'check subscription end date',
        'Subscription end date should be greater than offer expiry date',
        function (value?: string | null) {
          const offerExpiryDate = this.options?.context?.offerExpirationDate
          if (!isEmpty(value) && !isEmpty(offerExpiryDate)) {
            return (
              DateTime.fromISO(offerExpiryDate) < DateTime.fromISO(value || '')
            )
          }
          return true
        }
      )
  }
  if (planType === PlanType.SAAS_CONTRACT) {
    return Yup.string()
      .nullable()
      .test(
        'end-date-after-offer-expiration',
        'Please select a date after offer expiration date.',
        function (value?: string | null) {
          if (isEmpty(value)) return true
          const offerExpirationDate =
            this.parent?.offerExpirationDate ??
            this.options?.context?.offerExpirationDate
          if (!offerExpirationDate) return true
          return (
            DateTime.fromISO(offerExpirationDate as string) <
            DateTime.fromISO(value || '')
          )
        }
      )
      .test(
        'end-date-after-start-date',
        'End date must be after start date.',
        function (value?: string | null) {
          if (isEmpty(value)) return true
          const variableStartDate =
            this.parent?.variableStartDate ??
            this.options?.context?.variableStartDate
          const agreementStartDate =
            this.parent?.agreementStartDate ??
            this.options?.context?.agreementStartDate
          if (variableStartDate !== false || isEmpty(agreementStartDate)) {
            return true
          }
          return (
            DateTime.fromISO(value || '') >
            DateTime.fromISO(agreementStartDate as string)
          )
        }
      )
  }
  return Yup.string().nullable()
}

export function generateValidationSchema(
  planType: PlanType | undefined,
  containerPricingModel?: ContainerPricingModel,
  hasExternalBaseURL?: boolean,
  dimensionsOptions?: any
) {
  const isAMI = planType === PlanType.AMI

  const getAdditionalUsagePriceValidation = () => {
    if (isAMI && containerPricingModel !== ContainerPricingModel.CONTRACT) {
      return Yup.number()
        .nullable()
        .transform((value, originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined ||
            isNaN(value)
          ) {
            return undefined
          }
          return value
        })
        .typeError(enterPriceMsg)
        .required(enterPriceMsg)
        .test(
          'greater-than-zero',
          valueGreaterThanZeroMsg,
          function (value?: number | null) {
            return value !== null &&
              value !== undefined &&
              isNonEmptyNumber(value)
              ? !(value! < 0)
              : true
          }
        )
        .checkThreeDecimalpointValidation({
          message: threeDecimalMessage,
        })
    }
    return Yup.number().nullable().notRequired()
  }

  let containerOfferServiceLengthValidation
  if (containerPricingModel === ContainerPricingModel.CONTRACT) {
    containerOfferServiceLengthValidation = Yup.string()
      .required(monthsDurationMessage)
      .transform(value => value?.replace(/[DMYH]/gi, ''))
      .matches(monthsDurationRegex, {
        message: monthsDurationMessage,
        excludeEmptyString: true,
      })
  } else {
    containerOfferServiceLengthValidation = Yup.string()
      .required(daysDurationMessage)
      .transform(value => {
        const transformedValue = value?.replace(/[DMYH]/gi, '')
        return transformedValue
      })
      .matches(dyasDurationRegex, {
        message: daysDurationMessage,
        excludeEmptyString: true,
      })
  }

  const dimensionsValidation = Yup.array().when('flexiblePaymentTerms', {
    is: (flexiblePaymentTerms: boolean) => flexiblePaymentTerms,
    then: Yup.array()
      .of(
        Yup.object().shape({
          type: Yup.string(),
          name: Yup.string(),
          label:
            containerPricingModel === ContainerPricingModel.HOURLY
              ? Yup.string().notRequired()
              : Yup.string().required(
                  'Please choose at least one product dimension to proceed.'
                ),

          labelDescription: Yup.string(),
          quantity: Yup.number().when('isAdditionalUsageAllowed', {
            is: (isAdditionalUsageAllowed: boolean) =>
              isAMI || isAdditionalUsageAllowed === false,
            then: Yup.number()
              .typeError(enterQuantityMsg)
              .required(enterQuantityMsg)
              .positive(quantityNegativeMsg)
              .integer(quantityDecimalMsg)
              .min(1, quantityMoreThan0Msg),
            otherwise: Yup.number().nullable().notRequired(),
          }),
          isAdditionalUsageAllowed: Yup.boolean(),
          price:
            containerPricingModel !== ContainerPricingModel.CONTRACT
              ? Yup.number().when('isAdditionalUsageAllowed', {
                  is: true,
                  then: Yup.number()
                    .typeError(enterPriceMsg)
                    .required(enterPriceMsg)
                    .test(
                      'greater-than-zero',
                      'value must be greater than 0',
                      function (value?: number | null) {
                        return value !== null && isNonEmptyNumber(value)
                          ? !(value! <= 0)
                          : true
                      }
                    )
                    .checkThreeDecimalpointValidation({
                      message: threeDecimalMessage,
                    }),
                  otherwise: Yup.number().nullable().notRequired(),
                })
              : Yup.number().nullable().notRequired(),
          additionalUsagePrice: getAdditionalUsagePriceValidation(),
        })
      )
      .uniquePropertyCustomType(
        'label',
        'Display name must be unique',
        dimensionsOptions
      ),
    otherwise: Yup.array()
      .of(
        Yup.object().shape({
          type: Yup.string(),
          name: Yup.string(),
          label:
            containerPricingModel !== ContainerPricingModel.CONTRACT
              ? Yup.string().notRequired()
              : Yup.string().required(productDimMessage),
          labelDescription: Yup.string(),
          isAdditionalUsageAllowed: Yup.boolean(),
          price: Yup.number()
            .typeError(enterPriceMsg)
            .required(enterPriceMsg)
            .test(
              'greater-than-zero',
              'value must be greater than 0',
              function (value?: number | null) {
                return value !== null && isNonEmptyNumber(value)
                  ? !(value! <= 0)
                  : true
              }
            )
            .checkThreeDecimalpointValidation({
              message: threeDecimalMessage,
            }),
          additionalUsagePrice: getAdditionalUsagePriceValidation(),
        })
      )
      .uniquePropertyCustomType(
        'label',
        'Display name must be unique',
        dimensionsOptions
      ),
  })
  const dimensionsSaasValidation = Yup.array().when('flexiblePaymentTerms', {
    is: (flexiblePaymentTerms: boolean) => flexiblePaymentTerms,
    then: Yup.array()
      .of(
        Yup.object().shape({
          type: Yup.string(),
          name: Yup.string().when('type', {
            is: (type: string) => type !== 'standard',
            then: Yup.string().required('API identifier is required'),
            otherwise: Yup.string().nullable().notRequired(),
          }),
          label: Yup.string().when('type', {
            is: (type: string) => type !== 'standard',
            then: Yup.string()
              .required(productDimMessage)
              .matches(
                emptySpaceCharsAtStartAndEndRegex,
                `${mandatoryFieldMessage}.`
              )
              .max(80, 'Display name must be 1 to 80 characters'),
            otherwise: Yup.string().required(
              'Please choose at least one product dimension to proceed.'
            ),
          }),
          labelDescription: Yup.string().when('type', {
            is: (type: string) => type !== 'standard',
            then: Yup.string()
              .required('Description is required')
              .matches(
                emptySpaceCharsAtStartAndEndRegex,
                `${mandatoryFieldMessage}.`
              )
              .max(1000, 'Description must be 1 to 1000 characters'),
            otherwise: Yup.string().nullable().notRequired(),
          }),
          quantity: Yup.number().when('isAdditionalUsageAllowed', {
            is: (isAdditionalUsageAllowed: boolean) =>
              isAMI || isAdditionalUsageAllowed === false,
            then: Yup.number()
              .typeError(enterQuantityMsg)
              .required(enterQuantityMsg)
              .positive(quantityNegativeMsg)
              .integer(quantityDecimalMsg)
              .min(1, quantityMoreThan0Msg),
            otherwise: Yup.number().nullable().notRequired(),
          }),
          isAdditionalUsageAllowed: Yup.boolean(),
          additionalUsagePrice: getAdditionalUsagePriceValidation(),
          price: Yup.number()
            .nullable()
            .when('isAdditionalUsageAllowed', {
              is: true,
              then: Yup.number()
                .typeError(enterPriceMsg)
                .required(enterPriceMsg)
                .test(
                  'greater-than-or-equal-to-zero',
                  'value must be greater than or equal to 0',
                  function (value?: number | null) {
                    return value !== null && isNonEmptyNumber(value)
                      ? !(value! < 0)
                      : true
                  }
                )
                .checkThreeDecimalpointValidation({
                  message: threeDecimalMessage,
                }),
            }),
        })
      )
      .uniquePropertyCustomType(
        'label',
        'Display name must be unique',
        dimensionsOptions
      ),
    otherwise: Yup.array()
      .of(
        Yup.object().shape({
          type: Yup.string(),
          name: Yup.string().when('type', {
            is: (type: string) => type !== 'standard',
            then: Yup.string().required('API identifier is required'),
            otherwise: Yup.string().nullable().notRequired(),
          }),
          label: Yup.string().when('type', {
            is: (type: string) => type !== 'standard',
            then: Yup.string()
              .required(productDimMessage)
              .matches(
                emptySpaceCharsAtStartAndEndRegex,
                `${mandatoryFieldMessage}.`
              )
              .max(80, 'Display name must be 1 to 80 characters'),
            otherwise: Yup.string().required(
              'Please choose at least one product dimension to proceed.'
            ),
          }),
          labelDescription: Yup.string().when('type', {
            is: (type: string) => type !== 'standard',
            then: Yup.string()
              .required('Description is required')
              .matches(
                emptySpaceCharsAtStartAndEndRegex,
                `${mandatoryFieldMessage}.`
              )
              .max(1000, 'Description must be 1 to 1000 characters'),
            otherwise: Yup.string().nullable().notRequired(),
          }),
          isAdditionalUsageAllowed: Yup.boolean(),
          price: Yup.number()
            .typeError(enterPriceMsg)
            .required(enterPriceMsg)
            .test(
              'greater-than-or-equal-to-zero',
              'value must be greater than or equal to 0',
              function (value?: number | null) {
                return value !== null && isNonEmptyNumber(value)
                  ? !(value! < 0)
                  : true
              }
            )
            .checkThreeDecimalpointValidation({
              message: threeDecimalMessage,
            }),
          additionalUsagePrice: getAdditionalUsagePriceValidation(),
        })
      )
      .uniquePropertyCustomType(
        'label',
        'Display name must be unique',
        dimensionsOptions
      ),
  })

  let entitlementOptionValidation
  if (containerPricingModel === ContainerPricingModel.CONTRACT) {
    entitlementOptionValidation = Yup.string().when('flexiblePaymentTerms', {
      is: (flexiblePaymentTerms: boolean) => flexiblePaymentTerms,
      then: Yup.string(),
      otherwise: Yup.string().required(),
    })
  } else {
    entitlementOptionValidation = Yup.string().nullable()
  }

  const isContractPricing =
    containerPricingModel === ContainerPricingModel.CONTRACT

  const optOutDeadlineDaysValidation = Yup.number()
    .nullable()
    .transform((value, originalValue) => {
      if (
        originalValue === '' ||
        originalValue === null ||
        originalValue === undefined ||
        isNaN(value)
      ) {
        return undefined
      }
      return value
    })
    .when('renewalPricingTerms', {
      is: (renewalPricingTerms?: string) =>
        !!renewalPricingTerms && renewalPricingTerms !== 'NO_RENEWAL',
      then: Yup.number()
        .nullable()
        .transform((value, originalValue) => {
          if (
            originalValue === '' ||
            originalValue === null ||
            originalValue === undefined ||
            isNaN(value)
          ) {
            return undefined
          }
          return value
        })
        .required('Enter a number')
        .min(0, 'Enter a number')
        .test(
          'opt-out-less-than-contract-duration',
          OPT_OUT_DEADLINE_LT_DURATION_MSG,
          function (value?: number | null) {
            if (value === null || value === undefined) return true
            const durationDays = getContractDurationDays({
              serviceLength: this.parent?.serviceLength,
              isContract: isContractPricing || !containerPricingModel,
            })
            if (!durationDays) return true
            return value < durationDays
          }
        ),
      otherwise: Yup.number().nullable().optional().notRequired(),
    })

  // Figma Error stage — Renewal pricing terms
  const emptyNumberTransform = (value: number, originalValue: unknown) => {
    if (
      originalValue === '' ||
      originalValue === null ||
      originalValue === undefined ||
      (typeof value === 'number' && isNaN(value))
    ) {
      return undefined
    }
    return value
  }

  const renewalCountValidation = Yup.number()
    .nullable()
    .transform(emptyNumberTransform)
    .when(['renewalPricingTerms', 'renewalCountType'], {
      is: (renewalPricingTerms?: string, renewalCountType?: string) =>
        !!renewalPricingTerms &&
        renewalPricingTerms !== 'NO_RENEWAL' &&
        renewalCountType === 'DEFINITE',
      then: Yup.number()
        .nullable()
        .transform(emptyNumberTransform)
        .required('Enter a number')
        .integer('Enter a number between 1 and 60.')
        .min(1, 'Enter a number between 1 and 60.')
        .max(60, 'Enter a number between 1 and 60.'),
      otherwise: Yup.number().nullable().optional().notRequired(),
    })

  const priceUpliftPercentValidation = Yup.number()
    .nullable()
    .transform(emptyNumberTransform)
    .when(['renewalPricingTerms', 'priceUpliftType'], {
      is: (renewalPricingTerms?: string, priceUpliftType?: string) =>
        renewalPricingTerms === 'PRICE_UPLIFT' &&
        (priceUpliftType ?? 'FIXED') === 'FIXED',
      then: Yup.number()
        .nullable()
        .transform(emptyNumberTransform)
        .required('Enter a percentage')
        .min(0, 'Enter a percentage'),
      otherwise: Yup.number().nullable().optional().notRequired(),
    })

  const priceUpliftMinPercentValidation = Yup.number()
    .nullable()
    .transform(emptyNumberTransform)
    .when(['renewalPricingTerms', 'priceUpliftType'], {
      is: (renewalPricingTerms?: string, priceUpliftType?: string) =>
        renewalPricingTerms === 'PRICE_UPLIFT' && priceUpliftType === 'RANGE',
      then: Yup.number()
        .nullable()
        .transform(emptyNumberTransform)
        .required(THIS_FIELD_IS_REQUIRED)
        .min(0, 'Enter a percentage'),
      otherwise: Yup.number().nullable().optional().notRequired(),
    })

  const priceUpliftMaxPercentValidation = Yup.number()
    .nullable()
    .transform(emptyNumberTransform)
    .when(['renewalPricingTerms', 'priceUpliftType'], {
      is: (renewalPricingTerms?: string, priceUpliftType?: string) =>
        renewalPricingTerms === 'PRICE_UPLIFT' && priceUpliftType === 'RANGE',
      then: Yup.number()
        .nullable()
        .transform(emptyNumberTransform)
        .required(THIS_FIELD_IS_REQUIRED)
        .min(0, 'Enter a percentage')
        .test(
          'max-greater-than-min',
          'Maximum % cannot be smaller than or equal to Minimum %',
          function (value?: number | null) {
            if (value === null || value === undefined) return true
            const min = this.parent?.priceUpliftMinPercent
            if (min === null || min === undefined) return true
            return value > Number(min)
          }
        ),
      otherwise: Yup.number().nullable().optional().notRequired(),
    })

  const renewalCountTypeValidation = Yup.string()
    .nullable()
    .when('renewalPricingTerms', {
      is: (renewalPricingTerms?: string) =>
        !!renewalPricingTerms && renewalPricingTerms !== 'NO_RENEWAL',
      then: Yup.string()
        .nullable()
        .required('Select renewal count')
        .oneOf(['DEFINITE', 'INDEFINITE'], 'Select renewal count'),
      otherwise: Yup.string().nullable().optional().notRequired(),
    })

  const adjustmentDeadlineDaysValidation = Yup.number()
    .nullable()
    .transform(emptyNumberTransform)
    .when(['renewalPricingTerms', 'priceUpliftType'], {
      is: (renewalPricingTerms?: string, priceUpliftType?: string) =>
        renewalPricingTerms === 'PRICE_UPLIFT' && priceUpliftType === 'RANGE',
      then: Yup.number()
        .nullable()
        .transform(emptyNumberTransform)
        .required('Enter a number')
        .min(0, 'Enter a number')
        .test(
          'adjustment-greater-than-opt-out',
          'Adjustment days must be greater than Opt out days.',
          function (value?: number | null) {
            if (value === null || value === undefined) return true
            const optOut = this.parent?.optOutDeadlineDays
            if (optOut === null || optOut === undefined) return true
            return value > Number(optOut)
          }
        ),
      otherwise: Yup.number().nullable().optional().notRequired(),
    })

  const renewalInstallmentsValidation = Yup.array()
    .nullable()
    .when(['flexiblePaymentTerms', 'renewalPricingTerms', 'renewalCountType'], {
      is: (
        flexiblePaymentTerms?: boolean,
        terms?: string,
        countType?: string
      ) =>
        planType === PlanType.SAAS_CONTRACT &&
        !!flexiblePaymentTerms &&
        terms !== 'NO_RENEWAL' &&
        (countType === 'DEFINITE' || countType === 'INDEFINITE'),
      then: Yup.array()
        .nullable()
        .of(
          Yup.object().shape({
            monthsAfter: Yup.mixed().test(
              'months-after-required',
              RENEWAL_MONTHS_AFTER_REQUIRED,
              value => isRenewalMonthsAfterFilled(value)
            ),
            percentage: Yup.mixed(),
          })
        )
        .test(
          'percentages-sum-100',
          RENEWAL_PERCENT_SUM_ERROR,
          (rows?: { percentage?: number | null }[]) => {
            if (!rows?.length) return true
            const sum = rows.reduce(
              (total, row) => total + (Number(row?.percentage) || 0),
              0
            )
            return Math.abs(sum - 100) < 0.01
          }
        ),
      otherwise: Yup.array().nullable(),
    })

  const toOptionalNumber = (raw: unknown): number | null => {
    if (raw === '' || raw === null || raw === undefined) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  }

  const defaultUpliftPercentValidation = Yup.number()
    .nullable()
    .transform(emptyNumberTransform)
    .when(['renewalPricingTerms', 'priceUpliftType'], {
      is: (renewalPricingTerms?: string, priceUpliftType?: string) =>
        renewalPricingTerms === 'PRICE_UPLIFT' && priceUpliftType === 'RANGE',
      then: Yup.number()
        .nullable()
        .transform(emptyNumberTransform)
        .required('Enter a percentage')
        .min(0, 'Enter a percentage')
        .test(
          'default-within-range',
          'Must be within the minimum and maximum range.',
          function (value?: number | null) {
            if (value === null || value === undefined) return true
            const min = toOptionalNumber(this.parent?.priceUpliftMinPercent)
            const max = toOptionalNumber(this.parent?.priceUpliftMaxPercent)
            // Inclusive: min and max values are allowed
            if (min != null && value < min) return false
            if (max != null && value > max) return false
            return true
          }
        ),
      otherwise: Yup.number().nullable().optional().notRequired(),
    })

  const renewalPricingTermsValidations: Record<string, Yup.AnySchema> =
    planType === PlanType.SAAS_CONTRACT
      ? {
          optOutDeadlineDays: optOutDeadlineDaysValidation,
          renewalCountType: renewalCountTypeValidation,
          renewalCount: renewalCountValidation,
          priceUpliftPercent: priceUpliftPercentValidation,
          priceUpliftMinPercent: priceUpliftMinPercentValidation,
          priceUpliftMaxPercent: priceUpliftMaxPercentValidation,
          adjustmentDeadlineDays: adjustmentDeadlineDaysValidation,
          defaultUpliftPercent: defaultUpliftPercentValidation,
          renewalInstallments: renewalInstallmentsValidation,
        }
      : {}

  if (containerPricingModel && !isAMI) {
    return Yup.object().shape({
      companyName: Yup.string()
        .required('Company name cannot be empty.')
        .matches(
          emptySpaceCharsAtStartAndEndRegex,
          `${mandatoryFieldMessage}.`
        ),
      awsAccountId: Yup.string()
        .required(mandatoryFieldMessage)
        .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
        .min(12, awsAccountIdFieldValidationMessage)
        .max(12, awsAccountIdFieldValidationMessage)
        .matches(awsAccountIdRegex, awsRegexValidationMessage),
      privateOfferName: Yup.string()
        .required('Offer name cannot be empty.')
        .matches(
          emptySpaceCharsAtStartAndEndRegex,
          `${mandatoryFieldMessage}.`
        ),

      offerExpirationDate: Yup.string()
        .nullable()
        .required('Please provide the expiration date.'),
      emailCustomMessage: Yup.string()
        .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
        .test(
          'contains-new-line-characters',
          `${emptySpacesMessage}`,
          validateEmptyValueAndNewLines
        ),
      offerRecipients: buildOfferRecipientsValidation(
        containerOfferRecipientMessages
      ),
      flexiblePaymentTerms: Yup.boolean(),
      containerOfferServiceLength: containerOfferServiceLengthValidation,
      installments: Yup.array().when('flexiblePaymentTerms', {
        is: (flexiblePaymentTerms: boolean) => flexiblePaymentTerms,
        then: Yup.array()
          .of(
            Yup.object().shape({
              paymentDate: Yup.string()
                .nullable()
                .required('Payment date is required')
                .test(
                  'check contract duration date',
                  paymentDateValidationMessage,
                  function (value?: string | null) {
                    let duration =
                      this.options?.context?.containerOfferServiceLength
                    if (!duration) {
                      duration = '0'
                    }
                    const currentDate = DateTime.now()
                    const serviceEndDate =
                      containerPricingModel === ContainerPricingModel.CONTRACT
                        ? currentDate.plus({
                            months: parseInt(duration),
                          })
                        : duration &&
                            typeof duration === 'string' &&
                            duration.endsWith('Y')
                          ? currentDate.plus({
                              years: parseInt(duration),
                            })
                          : currentDate.plus({
                              days: parseInt(duration),
                            })
                    const serviceEndDateConverted = serviceEndDate.toISODate()
                    if (!isEmpty(value) && !isEmpty(serviceEndDateConverted)) {
                      return (
                        DateTime.fromISO(serviceEndDateConverted) >=
                        DateTime.fromISO(value || '')
                      )
                    }
                    return true
                  }
                ),
              amount: Yup.number()
                .typeError(enterNumberMessage)
                .required(enterAmountMessage)
                .test(
                  'check amount validation',
                  amountGreaterThanZeroMsg,
                  function (value?: number) {
                    return !isNil(value) && value >= 0
                  }
                )
                .checkThreeDecimalpointValidation({
                  message: threeDecimalMsg,
                }),
            })
          )
          .uniqueProperty('paymentDate', duplicateDateMessage)
          .installmentsValidation(
            'paymentDate',
            timeZone,
            onePaymentBeforeServiceDateMsg
          ),
      }),
      metaData: Yup.array().of(metaDataItemValidationSchema),
      renewal: Yup.mixed().test(
        'is-renewal-required',
        'Renewal is required when value is empty.',
        function (value: string | boolean) {
          if (value === undefined) {
            return false
          }
          return true
        }
      ),
      renewalChannel: Yup.string()
        .nullable()
        .when('renewal', {
          is: true,
          then: Yup.string().required('Renewal channel is required.'),
          otherwise: Yup.string().nullable(),
        }),
      ...renewalPricingTermsValidations,
      dimensions: dimensionsValidation,
      entitlementOption: entitlementOptionValidation,
      usageDuration: Yup.number().when('flexiblePaymentTerms', {
        is: (flexiblePaymentTerms?: boolean) =>
          !flexiblePaymentTerms &&
          containerPricingModel &&
          containerProductHourlyAndUsage.includes(containerPricingModel),
        then: Yup.number()
          .typeError(enterNumberMsg)
          .required(enterUsageDuration)
          .positive(usageDurationNegativeMsg)
          .integer(usageDurationDecimalMsg)
          .min(1, usageDurationMoreThan0Msg)
          .usageDurationValidation({ propertyPath: 'usageDuration' }),
        otherwise: Yup.number().nullable().optional().notRequired(),
      }),

      eulaType: Yup.string().required('EULA type is required.'),
      eulaFile: Yup.array().when('eulaType', {
        is: (eulaType: string) => eulaType === EulaType.CUSTOM,
        then: Yup.array()
          .required('EULA file is required when EULA type is custom.')
          .min(1, 'At least one EULA file is required.'),
        otherwise: Yup.array().optional().notRequired(),
      }),
      offerStakeholders: hasExternalBaseURL
        ? Yup.array().optional().notRequired()
        : Yup.array()
            .of(
              Yup.object().shape({
                email: Yup.string().required('Email cannot be empty.'),
              })
            )
            .min(1, 'At least one stakeholder is required.'),

      customOfferStakeholders: Yup.array().test(
        'Enter-valid-emails',
        'Enter valid emails',
        function (value?: CustomStakeholder[]) {
          if (!value) return true
          return value?.every(v =>
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
              v.email
            )
          )
        }
      ),
    })
  } else {
    return Yup.object().shape({
      companyName: Yup.string()
        .required(
          isAMI
            ? 'Buyer name cannot be empty.'
            : 'Company name cannot be empty.'
        )
        .matches(
          emptySpaceCharsAtStartAndEndRegex,
          `${mandatoryFieldMessage}.`
        ),
      awsAccountId: Yup.string()
        .required('Enter a non-empty value.')
        .matches(emptySpaceCharsAtStartAndEndRegex, `Enter a non-empty value.`)
        .min(12, awsAccountIdFieldValidationMessage)
        .max(12, awsAccountIdFieldValidationMessage)
        .matches(awsAccountIdRegex, awsRegexValidationMessage),
      privateOfferName: Yup.string()
        .required('Offer name cannot be empty.')
        .matches(
          emptySpaceCharsAtStartAndEndRegex,
          `${mandatoryFieldMessage}.`
        ),

      offerExpirationDate: Yup.string()
        .nullable()
        .required('Provide the expiration date.'),
      subscriptionEndDate:
        planType === PlanType.SAAS_CONTRACT
          ? generateSubscriptionEndDateSchema(planType).when(
              'contractDurationMode',
              {
                is: 'endDate',
                then: (schema: Yup.StringSchema<string | null | undefined>) =>
                  schema.required('Please provide the end date.'),
              }
            )
          : generateSubscriptionEndDateSchema(planType),
      emailCustomMessage: Yup.string()
        .transform((_, originalValue) =>
          originalValue === '' ? null : originalValue
        )
        .nullable()
        .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
        .test(
          'contains-new-line-characters',
          `${emptySpacesMessage}`,
          value => value === null || validateEmptyValueAndNewLines(value)
        ),
      offerRecipients: buildOfferRecipientsValidation(
        amiOfferRecipientMessages
      ),
      flexiblePaymentTerms: Yup.boolean(),
      variableStartDate: Yup.boolean().nullable(),
      agreementStartDate: Yup.string().when('variableStartDate', {
        is: false,
        then: Yup.string()
          .nullable()
          .required('Please provide the start date.')
          .test(
            'check speciffied date',
            'Please select a date after offer expiration date.',
            function (value?: string | null) {
              const offerExpirationDate =
                this.options?.context?.offerExpirationDate
              if (offerExpirationDate) {
                return (
                  DateTime.fromISO(offerExpirationDate) <
                  DateTime.fromISO(value || '')
                )
              }
              return true
            }
          ),
        otherwise: Yup.string().nullable(),
      }),
      serviceLength: Yup.string()
        .nullable()
        .when(['subscriptionEndDate', 'contractDurationMode'], {
          is: (endDate: string, contractDurationMode?: string) =>
            isEmpty(endDate) && contractDurationMode !== 'endDate' && !isAMI,
          then: Yup.string()
            .required(monthsDurationMessage)
            .transform(value => value?.replace(/[DMYH]/gi, ''))
            .matches(monthsDurationRegex, {
              message: monthsDurationMessage,
              excludeEmptyString: true,
            }),
          otherwise: Yup.string().nullable(),
        }),
      installments: Yup.array().when('flexiblePaymentTerms', {
        is: (flexiblePaymentTerms: boolean) => flexiblePaymentTerms,
        then: Yup.array()
          .of(
            Yup.object().shape({
              paymentDate: Yup.string()
                .nullable()
                .required('Payment date is required')
                .test(
                  'check contract duration date',
                  paymentDateValidationMessage,
                  function (value?: string | null) {
                    if (isAMI) {
                      return true
                    }

                    const duration = this.options?.context?.serviceLength
                    const currentDate = this.options?.context
                      ?.agreementStartDate
                      ? DateTime.fromISO(
                          this.options?.context?.agreementStartDate
                        )
                      : DateTime.now()
                    const serviceEndDate = duration
                      ? currentDate.plus({ months: parseInt(String(duration)) })
                      : DateTime.fromISO(
                          this.options?.context?.subscriptionEndDate || ''
                        )

                    const serviceEndDateConverted = serviceEndDate.isValid
                      ? serviceEndDate.toISODate()
                      : null
                    if (!isEmpty(value) && !isEmpty(serviceEndDateConverted)) {
                      return (
                        DateTime.fromISO(serviceEndDateConverted || '') >=
                        DateTime.fromISO(value || '')
                      )
                    }

                    return true
                  }
                ),
              amount: Yup.number()
                .typeError(enterNumberMessage)
                .required(enterAmountMessage)
                .test(
                  'check amount validation',
                  amountGreaterThanZeroMsg,
                  function (value?: number) {
                    return !isNil(value) && value >= 0
                  }
                )
                .checkDecimalpointValidation({
                  message: 'Only 2 decimal places allowed.',
                }),
            })
          )
          .uniqueProperty('paymentDate', duplicateDateMessage)
          .installmentsValidation(
            'paymentDate',
            timeZone,
            onePaymentBeforeServiceDateMsg
          )
          .test(
            'ami-payment-date-contract-validation',
            paymentDateValidationMessage,
            function (list: any) {
              if (!isAMI) {
                return true
              }
              const rootValues = this.parent as any

              if (!rootValues) {
                return true
              }

              const containerOfferServiceLength =
                rootValues.containerOfferServiceLength
              const offerExpirationDate = rootValues.offerExpirationDate

              // For AMI, contract end date = offerExpirationDate + containerOfferServiceLength
              if (!containerOfferServiceLength || !offerExpirationDate) {
                return true
              }

              const startDate = offerExpirationDate
              // Calculate duration in months from containerOfferServiceLength
              let durationInMonths: number
              if (typeof containerOfferServiceLength === 'number') {
                durationInMonths = containerOfferServiceLength
              } else {
                const match = containerOfferServiceLength
                  .toString()
                  .match(/^(\d+(?:\.\d+)?)([MYWD])?$/i)

                if (!match) {
                  const numericValue = parseFloat(containerOfferServiceLength)
                  if (!isNaN(numericValue) && numericValue > 0) {
                    durationInMonths = numericValue
                  } else {
                    return true
                  }
                } else {
                  const value = parseFloat(match[1])
                  const unit = match[2]?.toUpperCase()

                  switch (unit) {
                    case 'Y':
                      durationInMonths = value * 12
                      break
                    case 'M':
                      durationInMonths = value
                      break
                    case 'W':
                      durationInMonths = value / 4.345 // average weeks per month
                      break
                    case 'D':
                      durationInMonths = value / 30.44 // average days per month
                      break
                    default:
                      durationInMonths = value
                  }
                }
              }

              const startDateTime = DateTime.fromISO(startDate)
              if (!startDateTime.isValid) {
                return true
              }

              const serviceEndDate = startDateTime.plus({
                months: Math.round(durationInMonths * 100) / 100,
              })

              if (!serviceEndDate.isValid) {
                return true
              }

              const errors: any = []

              // Validate each payment date
              if (list && Array.isArray(list)) {
                list.forEach((item: any, index: number) => {
                  const paymentDate = item?.paymentDate
                  if (!isEmpty(paymentDate) && paymentDate) {
                    const paymentDateTime = DateTime.fromISO(paymentDate)
                    if (paymentDateTime.isValid) {
                      const serviceEndDateOnly = serviceEndDate.startOf('day')
                      const paymentDateOnly = paymentDateTime.startOf('day')

                      if (serviceEndDateOnly < paymentDateOnly) {
                        const errorPath = this.path
                          ? `${this.path}[${index}].paymentDate`
                          : `installments[${index}].paymentDate`
                        errors.push(
                          this.createError({
                            path: errorPath,
                            message: paymentDateValidationMessage,
                          })
                        )
                      }
                    }
                  }
                })
              }

              if (!_.isEmpty(errors)) {
                throw new Yup.ValidationError(errors)
              }

              return true
            }
          ),
      }),
      metaData: Yup.array().of(metaDataItemValidationSchema),
      renewal: Yup.mixed().test(
        'is-renewal-required',
        'Renewal is required when value is empty.',
        function (value: string | boolean) {
          if (value === undefined) {
            return false
          }
          return true
        }
      ),
      renewalChannel: Yup.string()
        .nullable()
        .when('renewal', {
          is: true,
          then: Yup.string().required('Renewal type is required.'),
          otherwise: Yup.string().nullable(),
        }),
      ...renewalPricingTermsValidations,
      dimensions: dimensionsSaasValidation,
      entitlementOption: entitlementOptionValidation,
      eulaType: Yup.string().required('EULA type is required.'),
      eulaFile: Yup.array().when('eulaType', {
        is: (eulaType: string) => eulaType === EulaType.CUSTOM,
        then: Yup.array()
          .required('EULA file is required when EULA type is custom.')
          .min(1, 'At least one EULA file is required.'),
        otherwise: Yup.array().optional().notRequired(),
      }),
      offerStakeholders: hasExternalBaseURL
        ? Yup.array().optional().notRequired()
        : Yup.array()
            .of(
              Yup.object().shape({
                email: Yup.string().required('Email cannot be empty.'),
              })
            )
            .min(1, 'At least one stakeholder is required.'),

      customOfferStakeholders: Yup.array().test(
        'Enter-valid-emails',
        'Enter valid emails',
        function (value?: CustomStakeholder[]) {
          if (!value) return true
          return value?.every(v =>
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
              v.email
            )
          )
        }
      ),
      ...((isAMI
        ? {
            durationType: Yup.string().when('pricingModel', {
              is: (pricingModel: string) =>
                pricingModel === 'installment-plan' ||
                pricingModel === 'upfront-payment',
              then: Yup.string().required('Duration type is required.'),
              otherwise: Yup.string().nullable().notRequired(),
            }),
            usageEndDate: Yup.string()
              .nullable()
              .when('pricingModel', {
                is: (pricingModel: string) =>
                  pricingModel === 'installment-plan',
                then: Yup.string().nullable().notRequired(),
                otherwise: Yup.string()
                  .nullable()
                  .required('Usage end date is required.')
                  .test(
                    'check-usage-end-date',
                    'Usage date must be after offer expiration date plus duration.',
                    function (value?: string | null) {
                      if (!value) {
                        return true
                      }

                      const offerExpirationDate =
                        this.options?.context?.offerExpirationDate
                      const durationLength = this.parent.durationLength
                      const durationType = this.parent.durationType

                      if (
                        !offerExpirationDate &&
                        (!durationLength || !durationType)
                      ) {
                        return true
                      }
                      const baseDate = offerExpirationDate
                        ? DateTime.fromISO(offerExpirationDate)
                        : DateTime.now()
                      const duration = durationLength || 0
                      const type = durationType || 'DAYS'

                      let calculatedEndDate: DateTime

                      switch (type.toUpperCase()) {
                        case 'DAYS':
                          calculatedEndDate = baseDate.plus({ days: duration })
                          break
                        case 'WEEKS':
                          calculatedEndDate = baseDate.plus({ weeks: duration })
                          break
                        case 'MONTHS':
                          calculatedEndDate = baseDate.plus({
                            months: duration,
                          })
                          break
                        case 'YEARS':
                          calculatedEndDate = baseDate.plus({ years: duration })
                          break
                        default:
                          calculatedEndDate = baseDate
                      }

                      const usageEndDate = DateTime.fromISO(value)

                      if (usageEndDate > calculatedEndDate) {
                        return true
                      }

                      const pricingModel = (
                        this.parent as { pricingModel?: string }
                      )?.pricingModel

                      const message =
                        pricingModel === 'usage-pricing'
                          ? 'Usage date must be after offer expiration date.'
                          : 'Usage date must be after offer expiration date plus duration.'

                      return this.createError({ message })
                    }
                  ),
              }),
            durationLength: Yup.number()
              .nullable()
              .when('pricingModel', {
                is: (pricingModel: string) => pricingModel === 'usage-pricing',
                then: Yup.number().nullable().notRequired(),
                otherwise: Yup.number()
                  .nullable()
                  .required('Duration is required.')
                  .test(
                    'duration-range',
                    'Duration must be within the valid range.',
                    function (value) {
                      if (value === null || value === undefined) return true
                      if (typeof value !== 'number' || isNaN(value)) return true

                      const { durationType } = this.parent
                      const type = durationType?.toUpperCase() || 'DAYS'

                      let min: number
                      let max: number
                      let unit: string

                      switch (type) {
                        case 'DAYS':
                          min = 1
                          max = 1095
                          unit = 'days'
                          break
                        case 'WEEKS':
                          min = 1
                          max = 157
                          unit = 'weeks'
                          break
                        case 'MONTHS':
                          min = 1
                          max = 36
                          unit = 'months'
                          break
                        case 'YEARS':
                          min = 1
                          max = 3
                          unit = 'years'
                          break
                        default:
                          return true
                      }

                      if (value < min || value > max) {
                        return this.createError({
                          message: `Duration must be between ${min} and ${max} ${unit}.`,
                        })
                      }

                      return true
                    }
                  ),
              }),
            offerDescription: Yup.string()
              .nullable()
              .matches(
                emptySpaceCharsAtStartAndEndRegex,
                `${mandatoryFieldMessage}.`
              )
              .max(
                255,
                'Provide offer description with fewer than 255 characters.'
              ),
          }
        : {}) as Record<string, Yup.AnySchema>),
    })
  }
}
